import type { CurrencyFormat } from "@rubyplay/effw-connector";
import type {
	CaptainUpDockContentHandler,
	CaptainUpDomController,
	CaptainUpModalContentHandler,
} from "@/integrations/captainup/CaptainUpBoot";
import {
	getCaptainUpMessagingProtocol,
	type CaptainUpPauseResumeHandler,
} from "@/integrations/captainup/CaptainUpMessagingProtocol";
import type {
	CaptainUpUiAction,
	CaptainUpUiKind,
	CaptainUpWidgetApi,
	TooltipPosition,
} from "@/integrations/captainup/CaptainUpTypes";
import { debugLog, debugWarn } from "@/utils/debug";

export type CaptainUpModalKind = "mission" | "tournament";
export type CaptainUpUserInteractionHandler = (action: string, data: unknown) => void;
export type CaptainUpEventHandler = (type: unknown, entity: unknown) => void;

function summarizeWidgetApi(api: CaptainUpWidgetApi | null): Record<string, boolean> {
	return {
		ui: typeof api?.ui === "function",
		onUserInteraction: typeof api?.onUserInteraction === "function",
		renderer: typeof api?.renderer === "function",
		setTheme: typeof api?.setTheme === "function",
		enableModules: typeof api?.enableModules === "function",
	};
}

function otherCaptainUpModalKind(kind: CaptainUpModalKind): CaptainUpModalKind {
	return kind === "mission" ? "tournament" : "mission";
}

export class CaptainUpAdapter {
	private readonly dom: CaptainUpDomController;

	public constructor(dom: CaptainUpDomController) {
		this.dom = dom;

		debugLog("[CaptainUpAdapter] constructed", {
			widgetApi: summarizeWidgetApi(this.getWidgetApi()),
		});
	}

	public attachDockWidget(kind: CaptainUpModalKind, host: HTMLElement): void {
		debugLog("[CaptainUpAdapter] attachDockWidget", {
			kind,
			hostId: host.id || null,
			hostAria: host.getAttribute?.("aria-label") ?? null,
		});

		if (kind === "mission") {
			this.dom.attachMission(host);
			return;
		}

		this.dom.attachTournament(host);
	}

	public parkDockWidget(kind: CaptainUpModalKind, parkingHost: HTMLElement): void {
		debugLog("[CaptainUpAdapter] parkDockWidget", {
			kind,
			hostId: parkingHost.id || null,
			hostAria: parkingHost.getAttribute?.("aria-label") ?? null,
		});

		if (kind === "mission") {
			this.dom.parkMission(parkingHost);
			return;
		}

		this.dom.parkTournament(parkingHost);
	}

	public parkAllDockWidgets(parkingHost: HTMLElement): void {
		debugLog("[CaptainUpAdapter] parkAllDockWidgets", {
			hostId: parkingHost.id || null,
			hostAria: parkingHost.getAttribute?.("aria-label") ?? null,
		});

		this.dom.parkMission(parkingHost);
		this.dom.parkTournament(parkingHost);
	}

	public attachModal(modalHost: HTMLElement): void {
		debugLog("[CaptainUpAdapter] attachModal", {
			hostId: modalHost.id || null,
			hostAria: modalHost.getAttribute?.("aria-label") ?? null,
		});

		this.dom.attachModal(modalHost);
	}

	public parkModal(parkingHost: HTMLElement): void {
		debugLog("[CaptainUpAdapter] parkModal", {
			hostId: parkingHost.id || null,
			hostAria: parkingHost.getAttribute?.("aria-label") ?? null,
		});

		this.dom.parkModal(parkingHost);
	}

	public openModal(kind: CaptainUpModalKind): void {
		debugLog("[CaptainUpAdapter] openModal", {
			kind,
		});

		this.invokeUi(kind, "open");
	}

	public closeModal(kind: CaptainUpModalKind): void {
		debugLog("[CaptainUpAdapter] closeModal", {
			kind,
		});

		this.invokeUi(kind, "close");
	}

	public closeAllModals(): void {
		debugLog("[CaptainUpAdapter] closeAllModals");

		this.closeModal("mission");
		this.closeModal("tournament");
	}

	public ensureExclusiveModal(kind: CaptainUpModalKind): void {
		const inactiveKind = otherCaptainUpModalKind(kind);

		debugLog("[CaptainUpAdapter] ensureExclusiveModal", {
			activeKind: kind,
			inactiveKind,
		});

		this.closeModal(inactiveKind);
		this.openModal(kind);
	}

	public subscribePauseResume(handler: CaptainUpPauseResumeHandler): () => void {
		return getCaptainUpMessagingProtocol().onPauseResume(handler);
	}

	public subscribeUserInteraction(handler: CaptainUpUserInteractionHandler): () => void {
		return getCaptainUpMessagingProtocol().onUserInteraction(handler);
	}

	public subscribeEvents(handler: CaptainUpEventHandler): () => void {
		return getCaptainUpMessagingProtocol().onEvent(handler);
	}

	public subscribeModalContent(handler: CaptainUpModalContentHandler): () => void {
		debugLog("[CaptainUpAdapter] subscribeModalContent");
		return this.dom.subscribeModalContent(handler);
	}

	public subscribeDockContent(handler: CaptainUpDockContentHandler): () => void {
		debugLog("[CaptainUpAdapter] subscribeDockContent");
		return this.dom.subscribeDockContent(handler);
	}

	public updateBet(bet: number, currencyFormat?: CurrencyFormat): void {
		getCaptainUpMessagingProtocol().sendBetUpdated(bet, currencyFormat);
	}

	public notifyGameReady(): void {
		getCaptainUpMessagingProtocol().sendGameReady();
	}

	public notifyPromotionStarted(): void {
		getCaptainUpMessagingProtocol().sendPromotionStarted();
	}

	public notifyPromotionEnded(): void {
		getCaptainUpMessagingProtocol().sendPromotionEnded();
	}

	public notifyBuyFeatureStarted(): void {
		getCaptainUpMessagingProtocol().sendBuyFeatureStarted();
	}

	public notifyBuyFeatureEnded(): void {
		getCaptainUpMessagingProtocol().sendBuyFeatureEnded();
	}

	public notifyPaidRoundStarted(): void {
		getCaptainUpMessagingProtocol().sendPaidRoundStarted();
	}

	public notifyPaidRoundEnded(): void {
		getCaptainUpMessagingProtocol().sendPaidRoundEnded();
	}

	public notifyRoundStarted(): void {
		getCaptainUpMessagingProtocol().sendRoundStarted();
	}

	public notifyRoundEnded(): void {
		getCaptainUpMessagingProtocol().sendRoundEnded();
	}

	public notifyAnimationsStarted(): void {
		getCaptainUpMessagingProtocol().sendAnimationsStarted();
	}

	public notifyAnimationsEnded(): void {
		getCaptainUpMessagingProtocol().sendAnimationsEnded();
	}

	public setTooltipPosition(position: TooltipPosition): void {
		getCaptainUpMessagingProtocol().sendSetTooltipPosition(position);
	}

	private getWidgetApi(): CaptainUpWidgetApi | null {
		if (typeof window === "undefined") {
			return null;
		}

		return window._CaptainUpWidget ?? null;
	}

	private invokeUi(kind: CaptainUpUiKind, action: CaptainUpUiAction): void {
		const api = this.getWidgetApi();

		if (!api) {
			debugWarn("[CaptainUpAdapter] widget api is missing; ui(kind, action) cannot be called", {
				kind,
				action,
			});
			return;
		}

		if (typeof api.ui !== "function") {
			debugWarn("[CaptainUpAdapter] widget api does not expose ui(kind, action)", {
				kind,
				action,
				widgetApi: summarizeWidgetApi(api),
			});
			return;
		}

		debugLog("[CaptainUpAdapter] invoking widget ui(kind, action)", {
			kind,
			action,
		});

		api.ui(kind, action);
	}
}